import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getSystemCanvasModules, getAllSystemCanvasControls, deleteSystemCanvasControlType } from './Store/CustomModule.action';
import SystemCanvasModulesList from './Components/system-canvas-modules-list';
import SystemCanvasControlsList from './Components/system-canvas-controls-list';
import SystemCanvasModulesFilter from './Components/system-canvas-modules-filter';
import SystemCanvasControlsFilter from './Components/system-canvas-controls-filter';
import AddToLibraryPanel from './Components/add-to-library-panel';
import ConfirmPopup from '../../../Components/ModalPopup/confirm-popup';
import Loader from '../../../Components/Utility/Loader/Loader';
import type { SystemCanvasModule, RootState, CardItem } from './custom-module-landing.types';
import type { SystemCanvasControl, ModuleCard } from './Components/types';
import { NewSearch } from '../../../Components/PIM/common/NewSearch';
import { NewCheckbox } from '../../../Components/PIM/common/NewCheckbox';
import CommonTabSwitch from '../../../Components/CommonTabSwitch/CommonTabSwitch';
import customModuleType from './Store/CustomModule.type';
import { getSubBrandAccess } from '../../SystemMasters/Store/SystemMasters.action';

/**
 * CustomModuleLanding component serves as the main landing page for the Custom Module feature.
 * Provides navigation between System Canvas Modules and System Canvas Controls with
 * functionality to view, select, and delete items.
 */
/** The two landing tabs, in the order CommonTabSwitch renders them. */
const CUSTOM_MODULE_TABS = [
  { eventKey: 'System Canvas Modules', eventName: 'System Canvas Modules' },
  { eventKey: 'System Canvas Controls', eventName: 'System Canvas Controls' },
];

const CustomModuleLanding: React.FC = () => {
  const { t } = useTranslation(['common', 'custom-module-system-canvas']);
  const history = useHistory();
  const location = useLocation();

  const tabParam = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('tab');
  }, [location.search]);

  const getTabFromUrl = useCallback((): string => {
    if (tabParam === 'system-canvas-modules') {
      return 'System Canvas Modules';
    }
    if (tabParam === 'system-canvas-controls') {
      return 'System Canvas Controls';
    }
    return 'System Canvas Modules';
  }, [tabParam]);

  const getUrlTabValue = useCallback((tab: string): string => {
    if (tab === 'System Canvas Modules') {
      return 'system-canvas-modules';
    }
    if (tab === 'System Canvas Controls') {
      return 'system-canvas-controls';
    }
    return 'system-canvas-modules';
  }, []);

  const [activeTab, setActiveTab] = useState<string>(getTabFromUrl());
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [controlToDelete, setControlToDelete] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [filters, setFilters] = useState<{
    brandId: string[];
    subBrandId: string[];
    productType: string[];
    catOneId: string[];
    catTwoId: string[];
  }>({
    brandId: [],
    subBrandId: [],
    productType: [],
    catOneId: [],
    catTwoId: [],
  });
  const [controlFilters, setControlFilters] = useState<{
    controlType: string[];
  }>({
    controlType: [],
  });
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [controlFilterActive, setControlFilterActive] = useState<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const PAGE_SIZE = 50;

  const { systemCanvasModules, systemCanvasControls } = useSelector((state: RootState) => state.CustomModuleReducer);

  /**
   * Transforms system canvas modules data into card format for display
   */
  const moduleCards = useMemo(() => {
    if (!systemCanvasModules || systemCanvasModules.length === 0) {
      return [];
    }
    return systemCanvasModules.map((module: SystemCanvasModule) => {
      const card: ModuleCard = {
        id: module._id,
        title: module.name || 'Untitled Module',
        brand: module.brand?.length ? module.brand.join(', ') : '--',
        subBrand: module.subBrand?.length ? module.subBrand.join(', ') : '--',
        productType: module.productType?.length ? module.productType.join(', ') : '--',
        category1: module.category1?.length ? module.category1.join(', ') : '--',
        category2: module.category2?.length ? module.category2.join(', ') : '--',
        isActive: module.isActive !== false,
        createdAt: module.createdAt ? new Date(module.createdAt).toLocaleDateString() : '--',
        modifiedAt: module.updatedAt ? new Date(module.updatedAt).toLocaleDateString() : '--',
      };
      if (module.iconUrl) {
        card.iconUrl = module.iconUrl;
      }
      return card;
    });
  }, [systemCanvasModules]);

  /**
   * Transforms system canvas controls data into card format for display
   */
  const controlCards = useMemo(() => {
    if (!systemCanvasControls || systemCanvasControls.length === 0) {
      return [];
    }
    return systemCanvasControls.map((control: SystemCanvasControl) => ({
      id: control._id,
      title: control.label || control.title || 'Untitled Control',
      controlType: control.controlType || 'unknown',
      location: control.moduleName || 'Unknown Location',
      createdAt: control.createdAt ? new Date(control.createdAt).toLocaleDateString() : '--',
      modifiedAt: control.updatedAt ? new Date(control.updatedAt).toLocaleDateString() : '--',
    }));
  }, [systemCanvasControls]);

  const currentCards = activeTab === 'System Canvas Modules' ? moduleCards : controlCards;

  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [showAddToLibrary, setShowAddToLibrary] = useState(false);
  const [createLibraryControlIds, setCreateLibraryControlIds] = useState<string[]>([]);


  /**
   * Fetches data with pagination and search support
   */
  const fetchData = useCallback(
    async (page: number, search: string = '', isNewSearch: boolean = false, filterParams?: typeof filters, controlFilterParams?: typeof controlFilters) => {
      try {
        if (page === 1) {
          setLoading(true);
        } else {
          setScrollLoading(true);
        }

        const params: any = {
          pageIndex: page,
          pageSize: PAGE_SIZE,
        };

        if (search?.trim()) {
          params.searchText = search.trim();
        }

        if (activeTab === 'System Canvas Modules') {
          const activeFilters = filterParams || filters;
          if (activeFilters.brandId.length > 0) {
            params.brandId = activeFilters.brandId;
          }
          if (activeFilters.subBrandId.length > 0) {
            params.subBrandId = activeFilters.subBrandId;
          }
          if (activeFilters.productType.length > 0) {
            params.productType = activeFilters.productType;
          }
          if (activeFilters.catOneId.length > 0) {
            params.catOneId = activeFilters.catOneId;
          }
          if (activeFilters.catTwoId.length > 0) {
            params.catTwoId = activeFilters.catTwoId;
          }
        } else if (activeTab === 'System Canvas Controls') {
          const activeControlFilters = controlFilterParams || controlFilters;
          if (activeControlFilters.controlType.length > 0) {
            params.controltype = activeControlFilters.controlType;
          }
        }

        let response: any;
        if (activeTab === 'System Canvas Modules') {
          response = await dispatch(getSystemCanvasModules({ ...params, isScrolled: !isNewSearch && page > 1 }));
        } else if (activeTab === 'System Canvas Controls') {
          response = await dispatch(getAllSystemCanvasControls({ ...params, isScrolled: !isNewSearch && page > 1 }));
        }

        if (response?.data) {
          const items = activeTab === 'System Canvas Modules' ? response.data.modules || [] : response.data.controls || [];
          const total = response.data.totalCount || 0;
          const currentLength = activeTab === 'System Canvas Modules' ? systemCanvasModules.length : systemCanvasControls.length;
          const newLength = isNewSearch || page === 1 ? items.length : currentLength + items.length;
          setHasMore(total > 0 && newLength < total);
        }
      } catch (error) {
        console.error('Error fetching data: ', error);
      } finally {
        setLoading(false);
        setScrollLoading(false);
      }
    },
    [dispatch, activeTab, systemCanvasModules.length, systemCanvasControls.length, filters, controlFilters],
  );

  /**
   * Handles search input change with debounce
   */
  const handleSearchTermChange = useCallback(
    (value: string) => {
      setSearchText(value);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setPageIndex(1);
        setHasMore(true);
        if (activeTab === 'System Canvas Modules') {
          fetchData(1, value, true, filters);
        } else {
          fetchData(1, value, true, undefined, controlFilters);
        }
      }, 1000);
    },
    [fetchData, filters, controlFilters, activeTab],
  );

  /**
   * Handles infinite scroll next page load
   */
  const handleNextPage = useCallback(() => {
    if (!scrollLoading && hasMore) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      if (activeTab === 'System Canvas Modules') {
        fetchData(nextPage, searchText, false, filters);
      } else {
        fetchData(nextPage, searchText, false, undefined, controlFilters);
      }
    }
  }, [pageIndex, searchText, hasMore, scrollLoading, fetchData, filters, controlFilters, activeTab]);

  /**
   * Refetches data for the currently active tab
   */
  const refetchCurrentTab = useCallback(async () => {
    setPageIndex(1);
    setHasMore(true);
    if (activeTab === 'System Canvas Modules') {
      await fetchData(1, searchText, true, filters);
    } else {
      await fetchData(1, searchText, true, undefined, controlFilters);
    }
  }, [fetchData, searchText, filters, controlFilters, activeTab]);

  /**
   * Handles filter application for modules
   */
  const handleApplyFilter = useCallback(
    (filterParams: typeof filters) => {
      setFilters(filterParams);
      const hasActiveFilters = Object.values(filterParams).some((val) => Array.isArray(val) && val.length > 0);
      setFilterActive(hasActiveFilters);
      setPageIndex(1);
      setHasMore(true);
      fetchData(1, searchText, true, filterParams);
    },
    [fetchData, searchText],
  );

  /**
   * Handles filter application for controls
   */
  const handleApplyControlFilter = useCallback(
    (filterParams: typeof controlFilters) => {
      setControlFilters(filterParams);
      const hasActiveFilters = filterParams.controlType.length > 0;
      setControlFilterActive(hasActiveFilters);
      setPageIndex(1);
      setHasMore(true);
      fetchData(1, searchText, true, undefined, filterParams);
    },
    [fetchData, searchText],
  );

  useEffect(() => {
    if (!tabParam) {
      const urlTabValue = getUrlTabValue(activeTab);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', urlTabValue);
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
      return;
    }

    const tabFromUrl = getTabFromUrl();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabParam, activeTab, location.pathname, location.search, history, getUrlTabValue, getTabFromUrl]);

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      const urlTabValue = getUrlTabValue(tab);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', urlTabValue);
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    },
    [history, location.pathname, location.search, getUrlTabValue],
  );

  useEffect(() => {
    setPageIndex(1);
    setHasMore(true);
    setSearchText('');
    setFilters({
      brandId: [],
      subBrandId: [],
      productType: [],
      catOneId: [],
      catTwoId: [],
    });
    setControlFilters({
      controlType: [],
    });
    setFilterActive(false);
    setControlFilterActive(false);
    fetchData(1, '', true);
  }, [dispatch, activeTab]);

  useEffect(() => {
    if (currentCards.length > 0) {
      const allSelected = currentCards.every((c: CardItem) => selectedIds[c.id]);
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [currentCards, selectedIds, activeTab]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Toggles selection state for all cards in the current view
   */
  const onToggleAll = (): void => {
    const next = !selectAll;
    setSelectAll(next);
    const updated: Record<string, boolean> = {};
    currentCards.forEach((c: CardItem) => {
      updated[c.id] = next;
    });
    setSelectedIds(updated);
  };

  /**
   * Toggles selection state for a single card
   * @param id - ID of the card to toggle
   */
  const onToggleOne = (id: string): void => {
    setSelectedIds((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const allSelected = currentCards.every((c: CardItem) => next[c.id]);
      setSelectAll(allSelected);
      return next;
    });
  };

  /**
   * Toggles the active status of a system canvas module
   * @param moduleId - ID of the module to update
   * @param isActive - New active status
   */
  const handleToggleActive = (moduleId: string, isActive: boolean): void => {
    dispatch({ type: customModuleType.UPDATE_SYSTEM_CANVAS_MODULE_ACTIVE_STATUS, payload: { moduleId, isActive } });
  };

  /**
   * Initiates the deletion process for a control
   * @param id - ID of the control to delete
   */
  const onDeleteControl = (id: string): void => {
    setControlToDelete(id);
    setShowDeleteConfirm(true);
  };

  /**
   * Confirms and executes the deletion of a control
   */
  const handleConfirmDelete = async (): Promise<void> => {
    if (controlToDelete) {
      try {
        await dispatch(deleteSystemCanvasControlType(controlToDelete, t));
        await refetchCurrentTab();
        setShowDeleteConfirm(false);
        setControlToDelete(null);
      } catch (error) {
        setShowDeleteConfirm(false);
        setControlToDelete(null);
      }
    }
  };

  /**
   * Opens the Add to Library panel with the selected control IDs
   * @param ids - Array of control IDs to add to a library
   */
  const handleCreateLibrary = useCallback((ids: string[]) => {
    setCreateLibraryControlIds(ids);
    setShowAddToLibrary(true);
  }, []);

  /**
   * Cancels the control deletion confirmation
   */
  const handleCancelDelete = (): void => {
    setShowDeleteConfirm(false);
    setControlToDelete(null);
  };

  useEffect(() => {
    dispatch(getSubBrandAccess());
  }, [dispatch]);

  return (
    <div className="">
      <div className="page-title-filter sticky ">
        <div className="title-container d-flex align-items-center">
          <h4 className="title p-0 m-auto fs-24">{t('CUSTOM_MODULES.LIBRARY.LANDING.SEARCH_TITLE', { ns: 'custom-module-system-canvas' })}</h4>
          <span className="cm-devider mx-2 mb-1 fs-24">{'|'}</span>
        </div>
        <div className="d-flex align-items-center">
          <NewSearch
            className="bombiix-search"
            value={searchText}
            onChange={handleSearchTermChange}
            placeholder={t('CUSTOM_MODULES.LIBRARY.LANDING.SEARCH_PLACEHOLDER', { ns: 'custom-module-system-canvas' })}
            ariaLabel={t('CUSTOM_MODULES.LIBRARY.LANDING.SEARCH_PLACEHOLDER', { ns: 'custom-module-system-canvas' })}
          />
          {activeTab === 'System Canvas Modules' && <SystemCanvasModulesFilter onApplyFilter={handleApplyFilter} filterActive={filterActive} />}
          {activeTab === 'System Canvas Controls' && <SystemCanvasControlsFilter onApplyFilter={handleApplyControlFilter} filterActive={controlFilterActive} />}
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between px-3 cm-landing-tab-row">
        <CommonTabSwitch masterActiveTab={activeTab} MenuList={CUSTOM_MODULE_TABS} activeTheTab={handleTabChange} />
        <div className="d-flex align-items-center">
          {!loading && currentCards.length > 0 && activeTab === 'System Canvas Modules' && (
            <NewCheckbox id="cmSelectAll" checked={selectAll} onChange={onToggleAll} label={t('COMMON_LABEL.SELECT_ALL')} labelClassName="text-nowrap" />
          )}
        </div>
      </div>

      <InfiniteScroll
        dataLength={currentCards.length}
        next={handleNextPage}
        hasMore={hasMore}
        loader={scrollLoading && <Loader />}
        className="fixed-col-container px-3"
      >
        {activeTab === 'System Canvas Modules' && (
          <SystemCanvasModulesList
            cards={moduleCards}
            loading={loading}
            selectedIds={selectedIds}
            onToggleOne={onToggleOne}
            onToggleActive={handleToggleActive}
            onRefetch={refetchCurrentTab}
          />
        )}
        {activeTab === 'System Canvas Controls' && (
          <SystemCanvasControlsList
            cards={controlCards}
            loading={loading}
            selectedIds={selectedIds}
            onToggleOne={onToggleOne}
            onDeleteControl={onDeleteControl}
            onRefetch={refetchCurrentTab}
            systemCanvasControls={systemCanvasControls}
            onCreateLibrary={handleCreateLibrary}
          />
        )}
      </InfiniteScroll>

      <ConfirmPopup
        onOpen={showDeleteConfirm}
        title={t('custom-module-system-canvas:CONFIRM.DELETE_TITLE')}
        message={t('custom-module-system-canvas:CONFIRM.DELETE_CONTROL_MESSAGE')}
        toggle={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {showAddToLibrary && (
        <AddToLibraryPanel
          selectedControlIds={createLibraryControlIds}
          onClose={() => setShowAddToLibrary(false)}
          onSuccess={() => {
            setShowAddToLibrary(false);
            setCreateLibraryControlIds([]);
          }}
        />
      )}
    </div>
  );
};

export default CustomModuleLanding;
